import { motion } from 'framer-motion'
import React from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { Button } from '@demandlocal/components'
import { useModal } from '../../../components/useModal'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from '../../InventoryCampaign/NewCampaign/CreateNewCampaignForm'
import { ReactComponent as RocketIcon } from '../../../images/icons/rocket_launch.svg'
import { startCase, upperCase } from 'lodash'
import { useCreateNonInventoryCampaign } from '../../../api/dealership/campaigns/useCreateNonInventoryCampaign'
import { useDealership } from '../../../store/zustand/dealershipStore'
import { toast } from 'react-hot-toast'
import { useEditLinkCampaign } from '../../../api/dealership/campaigns/useEditLinkCampaign'
import { useNavigate } from 'react-router'
import classNames from 'classnames'
import { queryClient } from '../../../api/queryClient'

const CreateNonInventoryCampaignLaunchPage = (): JSX.Element => {
  const formContext = useForm({
    mode: 'onSubmit',
  })

  const dealership = useDealership()
  const [containerHeight, setContainerHeight] = React.useState(0)
  const [formValues, setFormValues] = useAtom(createCampaignFormValuesAtom)
  const { handleSubmit } = formContext
  const { closeModal } = useModal()
  const navigate = useNavigate()
  const { mutateAsync: createAsanaTicket } = useCreateNonInventoryCampaign()
  const { mutateAsync: saveLinkCampaign, isLoading } = useEditLinkCampaign()
  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Creating campaign...')
    try {
      const vendor_campaign_name = `${startCase(dealership?.dealership)} - ${
        formValues.campaign_name
      } [${upperCase(formValues.product?.product_code)}${formValues.pacing_id}]`

      const response = await createAsanaTicket({
        data: {
          vendor_campaign_name: vendor_campaign_name,
          campaign_type: 'Non Inventory Campaign',
          notes: formValues?.notes,
          objective: startCase(formValues?.objective),
          start_date: formValues?.start_date,
          end_date: formValues?.end_date,
          budget: Number(formValues?.budget),
          zip: formValues?.target?.zip,
          audience: formValues?.target?.use_custom_audience
            ? formValues?.target?.audience.map(
                (audience) => `${audience.name} (${audience.size})`,
              )
            : 'None',
          primary_kpi: formValues?.kpi_goals?.primaryKpi,
          secondary_kpi: formValues?.kpi_goals?.secondaryKpi,
          tertiary_kpi: formValues?.kpi_goals?.tertiaryKpi,
          product: formValues.product?.product_name,
          product_code: formValues.product?.product_code,
          creatives: formValues?.product?.creatives,
        },
      })

      if (formValues.campaign_id) {
        await saveLinkCampaign({
          data: {
            vendor_campaign_name: vendor_campaign_name,
            is_incomplete: 0,
          },
          link_campaign_id: formValues.campaign_id,
        })
      }

      await queryClient.refetchQueries([
        'dealership',
        dealership?.id,
        'campaigns',
        'list',
      ])
      await queryClient.refetchQueries([
        'dealership',
        dealership?.id,
        'campaigns',
        'incomplete',
      ])

      toast.success('Campaign created!', { id: toastId })
      navigate('..')
    } catch (err) {
      toast.error('An error has occured', { id: toastId })
    }
  }

  return (
    <FormProvider {...formContext}>
      <motion.form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col overflow-auto pb-20'}>
        <motion.div
          ref={(_ref) => {
            if (containerHeight === 0 && _ref?.clientHeight)
              setContainerHeight(_ref?.clientHeight ?? 0)
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={classNames(
            'flex flex-1 flex-col items-center gap-10 pt-8 pb-8 overflow-auto',
            {
              'max-h-[auto]': containerHeight === 0,
              'max-h-full]': containerHeight !== 0,
            },
          )}>
          <div className={'flex self-center'}>
            <RocketIcon />
          </div>
          <div className={'flex flex-col items-center gap-3'}>
            <span className={'font-bold text-2xl'}>Ready to launch!</span>
            <span className={'text-lg text-web-gray'}>This is your moment of glory</span>
          </div>
          <div className={'flex flex-col items-center gap-3'}>
            <Button variant={'primary'} animate={false} disabled={isLoading}>
              Create campaign!
            </Button>
            <button
              className={
                'text-md font-bold border-2 rounded-xl border-white hover:border-gainsboro-gray p-3'
              }
              onClick={(e) => {
                e.preventDefault()
                navigate('..')
              }}>
              Hold on, not yet
            </button>
          </div>
        </motion.div>
      </motion.form>
    </FormProvider>
  )
}

export default CreateNonInventoryCampaignLaunchPage
